import { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useOutletContext, Link } from "react-router-dom";
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const COLORS = {
  closed: "#22c55e",
  active: "#3b82f6",
  inactive: "#f59e0b",
};

function pct(val, total) {
  return ((val / total) * 100).toFixed(1);
}

function IndustryBadge({ industry }) {
  const colors = [
    { bg: "#eff6ff", color: "#1e40af" },
    { bg: "#f0fdf4", color: "#166534" },
    { bg: "#fefce8", color: "#854d0e" },
    { bg: "#fdf4ff", color: "#6b21a8" },
    { bg: "#fff1f2", color: "#9f1239" },
    { bg: "#ecfeff", color: "#155e75" },
    { bg: "#f0f9ff", color: "#0369a1" },
  ];

  const getColor = (str) => {
    if (!str) return colors[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const c = getColor(industry);

  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        background: c.bg,
        color: c.color,
        borderRadius: 99,
        padding: "3px 10px",
        display: "inline-block",
        whiteSpace: "nowrap",
      }}
    >
      {industry || "N/A"}
    </span>
  );
}

function ClosedRolesModal({ year, roles = [], onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(2px)",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "min(680px, 100%)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "0.5px solid rgba(0,0,0,0.07)",
            flexShrink: 0,
          }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>
            Closed Roles — {year}
          </h2>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "1.5px solid #d1d5db",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              color: "#6b7280",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* Desktop Table — hidden on mobile */}
          <div className="modal-table-desktop">
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1.5fr 1.5fr 1.2fr 2.2fr 110px",
                padding: "10px 20px",
                background: "#f9fafb",
                borderBottom: "0.5px solid rgba(0,0,0,0.06)",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {["Role", "Client", "Function", "DOJ", "Candidate", "Closed"].map((h) => (
                <span key={h} style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500 }}>
                  {h}
                </span>
              ))}
            </div>

            {roles.map((r, i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 1.5fr 1.2fr 2.2fr 110px",
                  padding: "13px 20px",
                  borderBottom: "0.5px solid rgba(0,0,0,0.05)",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                  {r.closed_role}
                </span>
                <span style={{ fontSize: 13, color: "#9ca3af" }}>{r.client}</span>
                <IndustryBadge industry={r.industries} />
                <span style={{ fontSize: 13, color: "#6b7280" }}>{r.joining_date}</span>
                <span style={{ fontSize: 13 }}>
                  {r.candidate_id ? (
                    <Link
                      to={`/candidates/view/${r.candidate_id}`}
                      style={{ textDecoration: "none", color: "#2563eb", fontWeight: 500 }}
                    >
                      👤 {r.candidate_name}
                    </Link>
                  ) : (
                    <span style={{ color: "#9ca3af" }}>Not Joined</span>
                  )}
                </span>
                <span style={{ fontSize: 13, color: "#6b7280" }}>{r.closed_date}</span>
              </div>
            ))}
          </div>

          {/* Mobile Cards — shown only on small screens */}
          <div className="modal-cards-mobile">
            {roles.map((r, i) => (
              <div
                key={i}
                style={{
                  padding: "14px 16px",
                  borderBottom: "0.5px solid rgba(0,0,0,0.07)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#111827", flex: 1, marginRight: 8 }}>
                    {r.closed_role}
                  </span>
                  <IndustryBadge industry={r.industries} />
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    <span style={{ color: "#6b7280" }}>Client:</span> {r.client}
                  </span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    <span style={{ color: "#6b7280" }}>DOJ:</span> {r.joining_date}
                  </span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    <span style={{ color: "#6b7280" }}>Closed:</span> {r.closed_date}
                  </span>
                </div>
                <div style={{ fontSize: 12 }}>
                  {r.candidate_id ? (
                    <Link
                      to={`/candidates/view/${r.candidate_id}`}
                      style={{ textDecoration: "none", color: "#2563eb", fontWeight: 500 }}
                    >
                      👤 {r.candidate_name}
                    </Link>
                  ) : (
                    <span style={{ color: "#9ca3af" }}>Candidate: Not Joined</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .modal-table-desktop { display: block; }
        .modal-cards-mobile { display: none; }
        @media (max-width: 600px) {
          .modal-table-desktop { display: none; }
          .modal-cards-mobile { display: block; }
        }
      `}</style>
    </div>
  );
}

function ProgressRow({ color, icon, label, count, percent }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: color + "18",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{label}</div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>{count} roles</div>
          </div>
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginLeft: 8 }}>
          {percent}%
        </div>
      </div>
      <div style={{ height: 8, background: "#e5e7eb", borderRadius: 99 }}>
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            background: color,
            borderRadius: 99,
            transition: "width 0.7s ease",
          }}
        />
      </div>
    </div>
  );
}

export default function PerformanceByYear() {
  const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const [selectedYear, setSelectedYear] = useState("FY 2025-26");
  const [modalOpen, setModalOpen] = useState(false);
  const yearOptions = [
    "FY 2025-26",
    "FY 2024-25",
    "FY 2023-24",
    "FY 2022-23",
    "FY 2021-22",
  ];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setGlobalLoading(true);
        const res = await fetch(
          `${REACT_APP_API_BASE_URL}/workstation/performance_by_year?year=${selectedYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const result = await res.json();
        setData(result);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        setGlobalLoading(false);
      }
    };
    fetchData();
  }, [selectedYear]);

  if (loading || !data) return <div style={{ padding: 32, color: "#9ca3af", fontFamily: "Inter, sans-serif" }}>Loading...</div>;

  const closedPct = pct(data.closed, data.total);
  const activePct = pct(data.active, data.total);
  const inactivePct = pct(data.inactive, data.total);

  const chartData = {
    labels: [""],
    datasets: [
      { label: "Closed", data: [data.closed], backgroundColor: COLORS.closed, borderRadius: 4, barThickness: 24 },
      { label: "Active", data: [data.active], backgroundColor: COLORS.active, borderRadius: 4, barThickness: 24 },
      { label: "Inactive", data: [data.inactive], backgroundColor: COLORS.inactive, borderRadius: 4, barThickness: 24 },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}` } },
    },
    scales: {
      x: { stacked: true, display: false },
      y: { stacked: true, display: false },
    },
    animation: { duration: 700, easing: "easeInOutQuart" },
  };

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100vh",
        padding: "24px 16px",
        fontFamily: "Inter, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        @media (min-width: 640px) {
          .perf-outer-padding { padding: 28px 24px !important; }
        }
        @media (min-width: 1024px) {
          .perf-outer-padding { padding: 30px 32px !important; }
        }

        /* Header row: stacks on mobile, row on sm+ */
        .perf-header {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }
        @media (min-width: 480px) {
          .perf-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 24px;
          }
        }

        /* Summary card: stacks on mobile, row on sm+ */
        .summary-card {
          display: flex;
          flex-direction: column;
          gap: 16px;
          background: #fff;
          border-radius: 16px;
          padding: 18px;
          margin-bottom: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        @media (min-width: 640px) {
          .summary-card {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 20px 24px;
            gap: 0;
          }
        }

        /* Summary stats: row on mobile (2 cols), inline on sm+ */
        .summary-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (min-width: 640px) {
          .summary-stats {
            display: contents;
          }
        }

        /* View button: full width on mobile, auto on sm+ */
        .view-btn {
          width: 100%;
          justify-content: center;
        }
        @media (min-width: 640px) {
          .view-btn {
            width: auto;
          }
        }

        /* Stat item */
        .summary-stat {
          background: #f9fafb;
          border-radius: 10px;
          padding: 10px 14px;
          text-align: center;
        }
        @media (min-width: 640px) {
          .summary-stat {
            background: transparent;
            border-radius: 0;
            padding: 0;
          }
        }
      `}</style>

      <div className="perf-outer-padding" style={{ width: "100%", boxSizing: "border-box" }}>
        {/* Header */}
        <div className="perf-header">
          <div>
            <h1 style={{ fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 800, color: "#111827", margin: 0 }}>
              Performance Overview
            </h1>
            <p style={{ fontSize: 13, color: "#9ca3af", marginTop: 4, marginBottom: 0 }}>
              Annual role allocation split by year
            </p>
          </div>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{
              fontSize: 14,
              color: "#374151",
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 10,
              padding: "8px 14px",
              cursor: "pointer",
              outline: "none",
              fontFamily: "inherit",
              alignSelf: "flex-start",
              flexShrink: 0,
            }}
          >
            {yearOptions.map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>

        {/* Summary Card */}
        <div className="summary-card">
          {/* Closed count */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                background: "#dcfce7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              ✅
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Roles Closed in {selectedYear}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>
                {data.closed}
              </div>
            </div>
          </div>

          {/* Stats: grid on mobile, inline on desktop */}
          <div className="summary-stats">
            <div className="summary-stat">
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Close Rate</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>{closedPct}%</div>
            </div>
            <div className="summary-stat">
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Total Allocated</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>{data.total}</div>
            </div>
          </div>

          {/* View button */}
          <button
            onClick={() => setModalOpen(true)}
            className="view-btn"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              border: "1.5px solid #22c55e",
              color: "#22c55e",
              background: "transparent",
              padding: "9px 18px",
              borderRadius: 99,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            👁 View closed roles
          </button>
        </div>

        {/* Annual Split Card */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "20px 18px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <h2 style={{ fontSize: "clamp(15px, 3vw, 18px)", fontWeight: 700, color: "#111827", marginBottom: 20, marginTop: 0 }}>
            Annual Split — {selectedYear}
          </h2>

          {/* Stacked Bar */}
          <div style={{ position: "relative", height: 48, marginBottom: 24 }}>
            <Bar data={chartData} options={chartOptions} />
          </div>

          {/* Progress Rows */}
          <ProgressRow color={COLORS.closed} icon="✔️" label="Closed" count={data.closed} percent={closedPct} />
          <ProgressRow color={COLORS.active} icon="📈" label="Active" count={data.active} percent={activePct} />
          <ProgressRow color={COLORS.inactive} icon="⭕" label="Inactive" count={data.inactive} percent={inactivePct} />
        </div>
      </div>

      {modalOpen && (
        <ClosedRolesModal
          year={selectedYear}
          roles={data.closed_roles || []}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
